import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getGateways, addGateway, toggleGateway, deleteGateway, checkGatewayCap } from '../api';

function GatewaysTab({ socket }) {
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    username: '', password: '', gateway_id: '',
    monthly_cap: '', gateway_name: '',
  });

  // Auto-parse gateway name into Processor - Bank - DBA - Company
  const parsedName = useMemo(() => {
    const name = form.gateway_name || '';
    const parts = name.split(' - ').map(p => p.trim()).filter(Boolean);
    return {
      processor: parts[0] || '',
      bank: parts[1] || '',
      dba: parts[2] || '',
      company: parts[3] || '',
    };
  }, [form.gateway_name]);

  const fetchGateways = useCallback(async () => {
    try {
      const res = await getGateways();
      if (res.success) setGateways(res.data);
    } catch (err) {
      console.error('Failed to fetch gateways:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGateways();
    const interval = setInterval(fetchGateways, 15000);
    return () => clearInterval(interval);
  }, [fetchGateways]);

  useEffect(() => {
    if (socket) {
      socket.on('gateway_cap_updated', fetchGateways);
      socket.on('caps_updated', fetchGateways);
      return () => {
        socket.off('gateway_cap_updated', fetchGateways);
        socket.off('caps_updated', fetchGateways);
      };
    }
  }, [socket, fetchGateways]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setError('');

    try {
      await addGateway({
        username: form.username,
        password: form.password,
        gateway_id: form.gateway_id,
        monthly_cap: form.monthly_cap,
        gateway_name: form.gateway_name,
      });
      setForm({ username: '', password: '', gateway_id: '', monthly_cap: '', gateway_name: '' });
      setShowForm(false);
      fetchGateways();
    } catch (err) {
      setError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await toggleGateway(id);
      fetchGateways();
    } catch (err) {
      console.error('Toggle error:', err);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete gateway "${name}"?`)) return;
    try {
      await deleteGateway(id);
      fetchGateways();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleCheckCap = async (id) => {
    try {
      await checkGatewayCap(id);
      fetchGateways();
    } catch (err) {
      console.error('CAP check error:', err);
    }
  };

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '—';
    return `$${parseFloat(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getCapPercentage = (gw) => {
    if (!gw.remaining_cap || !gw.monthly_cap || gw.monthly_cap === 0) return null;
    return ((gw.remaining_cap / gw.monthly_cap) * 100).toFixed(1);
  };

  const getCapColor = (pct) => {
    if (pct === null) return 'bg-slate-600';
    if (pct > 60) return 'bg-green-500';
    if (pct > 30) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Gateways</h2>
          <p className="text-slate-400 text-sm mt-1">{gateways.length} gateway(s) configured</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={showForm ? "M6 18L18 6M6 6l12 12" : "M12 4v16m8-8H4"} />
          </svg>
          <span>{showForm ? 'Cancel' : 'Add Gateway'}</span>
        </button>
      </div>

      {/* Add Gateway Form */}
      {showForm && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">New Gateway</h3>
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Username</label>
                <input
                  type="text" required value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  placeholder="NMI username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Password</label>
                <input
                  type="password" required value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  placeholder="NMI password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Gateway ID</label>
                <input
                  type="text" required value={form.gateway_id}
                  onChange={(e) => setForm({ ...form, gateway_id: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  placeholder="Unique identifier"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Monthly CAP ($)</label>
                <input
                  type="number" step="0.01" required value={form.monthly_cap}
                  onChange={(e) => setForm({ ...form, monthly_cap: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  placeholder="50000.00"
                />
              </div>
            </div>

            {/* Gateway Name - single field with auto-parse */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Gateway Name <span className="text-slate-500 font-normal">(format: Processor - Bank - DBA - Company)</span>
              </label>
              <input
                type="text" required value={form.gateway_name}
                onChange={(e) => setForm({ ...form, gateway_name: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                placeholder="Celero - BMO Harris - Travelsafepro - Hill"
              />
              {/* Auto-parse preview */}
              {form.gateway_name && (
                <div className="mt-2 flex flex-wrap gap-3 text-xs">
                  <div className="flex items-center space-x-1.5">
                    <span className="text-slate-500">Processeur:</span>
                    <span className={`px-2 py-0.5 rounded ${parsedName.processor ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                      {parsedName.processor || '(manquant)'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <span className="text-slate-500">Banque:</span>
                    <span className={`px-2 py-0.5 rounded ${parsedName.bank ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                      {parsedName.bank || '(manquant)'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <span className="text-slate-500">DBA:</span>
                    <span className={`px-2 py-0.5 rounded ${parsedName.dba ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
                      {parsedName.dba || '(vide)'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <span className="text-slate-500">Company:</span>
                    <span className={`px-2 py-0.5 rounded ${parsedName.company ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
                      {parsedName.company || '(vide)'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button
                type="submit" disabled={formLoading || !parsedName.processor}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-800 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
              >
                {formLoading ? (
                  <span className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    <span>Adding & checking CAP...</span>
                  </span>
                ) : 'Add Gateway'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Gateways List */}
      {gateways.length === 0 ? (
        <div className="text-center py-16 bg-slate-800/50 rounded-xl border border-slate-700">
          <svg className="w-12 h-12 text-slate-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
          </svg>
          <p className="text-slate-400">No gateways configured yet</p>
          <p className="text-slate-500 text-sm mt-1">Add your first NMI gateway to get started</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {gateways.map((gw) => {
            const capPct = getCapPercentage(gw);
            return (
              <div
                key={gw.id}
                className={`bg-slate-800 rounded-xl border transition-colors ${
                  gw.is_active ? 'border-slate-700' : 'border-slate-700/50 opacity-60'
                }`}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-1">
                        <h3 className="text-lg font-semibold text-white">
                          {gw.processor} - {gw.bank}{gw.dba ? ` - ${gw.dba}` : ''}{gw.company ? ` - ${gw.company}` : ''}
                        </h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          gw.is_active ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                        }`}>
                          {gw.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-slate-400 text-sm">
                        ID: <span className="text-slate-300 font-mono">{gw.gateway_id}</span>
                        {' · '}
                        User: <span className="text-slate-300">{gw.username}</span>
                      </p>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleCheckCap(gw.id)}
                        className="p-2 text-slate-400 hover:text-primary-400 hover:bg-slate-700 rounded-lg transition-colors"
                        title="Refresh CAP"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleToggle(gw.id)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          gw.is_active ? 'bg-green-500' : 'bg-slate-600'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          gw.is_active ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                      <button
                        onClick={() => handleDelete(gw.id, gw.display_name)}
                        className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* CAP Progress */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="text-slate-400">Monthly CAP</span>
                      <span className="text-slate-300">
                        {formatCurrency(gw.remaining_cap)} remaining of {formatCurrency(gw.monthly_cap)}
                        {capPct !== null && (
                          <span className="text-slate-500 ml-1">({capPct}%)</span>
                        )}
                      </span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${getCapColor(capPct)}`}
                        style={{ width: `${capPct || 0}%` }}
                      />
                    </div>
                    {gw.last_cap_check && (
                      <p className="text-xs text-slate-500 mt-1">
                        Last checked: {new Date(gw.last_cap_check).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default GatewaysTab;
